import React, { useState, useEffect } from 'react';
import './CreateEditCouponModal.css';

const CreateEditCouponModal = ({ coupon, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discountType: 'percentage',
    discountValue: '',
    maxDiscount: '',
    minOrderAmount: '',
    validFrom: '',
    validTill: '',
    usageLimit: null,
    unlimitedUses: true,
    perUserLimit: false,
    applicableTo: 'all',
    applicableCategories: [],
    terms: '',
    isActive: true
  });

  const [errors, setErrors] = useState({});
  
  // Available categories (you can fetch these from your categories table)
  const availableCategories = ['Pizza', 'Burgers', 'Chicken', 'Chinese', 'Drinks', 'Desserts'];

  useEffect(() => {
    if (coupon) {
      setFormData({
        code: coupon.code || '',
        description: coupon.description || '',
        discountType: coupon.discountType || 'percentage',
        discountValue: coupon.discountValue || '',
        maxDiscount: coupon.maxDiscount || '',
        minOrderAmount: coupon.minOrderAmount || '',
        validFrom: coupon.validFrom || '',
        validTill: coupon.validTill || '',
        usageLimit: coupon.usageLimit || null,
        unlimitedUses: !coupon.usageLimit,
        perUserLimit: coupon.perUserLimit || false,
        applicableTo: coupon.applicableTo || 'all',
        applicableCategories: coupon.applicableCategories || [],
        terms: coupon.terms || '',
        isActive: coupon.isActive !== undefined ? coupon.isActive : true
      });
    }
  }, [coupon]);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData(prev => ({ ...prev, code }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.code.trim()) newErrors.code = 'Coupon code is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.discountValue) newErrors.discountValue = 'Discount value is required';
    if (!formData.validFrom) newErrors.validFrom = 'Valid from date is required';
    if (!formData.validTill) newErrors.validTill = 'Valid till date is required';
    
    // Validate categories if applicable
    if (formData.applicableTo === 'categories' && formData.applicableCategories.length === 0) {
      newErrors.applicableCategories = 'Please select at least one category';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const couponData = {
      code: formData.code.toUpperCase().trim(),
      description: formData.description || '',
      discountType: formData.discountType,
      discountValue: parseFloat(formData.discountValue),
      maxDiscount: formData.discountType === 'percentage' && formData.maxDiscount 
        ? parseFloat(formData.maxDiscount) 
        : null,
      minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : null,
      validFrom: formData.validFrom,
      validTill: formData.validTill,
      usageLimit: formData.unlimitedUses ? null : (formData.usageLimit ? parseInt(formData.usageLimit) : null),
      perUserLimit: formData.perUserLimit || false,
      applicableTo: formData.applicableTo || 'all',
      applicableCategories: formData.applicableTo === 'categories' ? formData.applicableCategories || [] : null,
      terms: formData.terms || '',
      isActive: formData.isActive !== undefined ? formData.isActive : true
    };

    onSave(couponData);
  };

  return (
    <div className="coupon-modal-overlay" onClick={onClose}>
      <div className="coupon-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {coupon ? 'Edit Coupon' : 'Create New Coupon'}
          </h2>
          <button className="modal-close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="coupon-form">
          <div className="form-section">
            <label className="form-label">
              Coupon Code <span className="required">*</span>
            </label>
            <div className="code-input-group">
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleChange}
                className={`form-input ${errors.code ? 'error' : ''}`}
                placeholder="e.g., SUMMER2024"
                style={{ textTransform: 'uppercase' }}
              />
              <button
                type="button"
                className="auto-generate-btn"
                onClick={generateCode}
              >
                Auto Generate
              </button>
            </div>
            {errors.code && <span className="error-message">{errors.code}</span>}
            <div className="helper-text">Use uppercase letters and numbers only</div>
          </div>

          <div className="form-section">
            <label className="form-label">Discount Type <span className="required">*</span></label>
            <div className="radio-group-horizontal">
              <label className="radio-option">
                <input
                  type="radio"
                  name="discountType"
                  value="percentage"
                  checked={formData.discountType === 'percentage'}
                  onChange={handleChange}
                />
                <span>Percentage</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="discountType"
                  value="fixed"
                  checked={formData.discountType === 'fixed'}
                  onChange={handleChange}
                />
                <span>Fixed Amount</span>
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-section">
              <label className="form-label">
                Discount Value <span className="required">*</span>
              </label>
              <div className="input-with-suffix">
                <input
                  type="number"
                  name="discountValue"
                  value={formData.discountValue}
                  onChange={handleChange}
                  className={`form-input ${errors.discountValue ? 'error' : ''}`}
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
                <span className="input-suffix">
                  {formData.discountType === 'percentage' ? '%' : 'PKR'}
                </span>
              </div>
              {errors.discountValue && <span className="error-message">{errors.discountValue}</span>}
            </div>

            {formData.discountType === 'percentage' && (
              <div className="form-section">
                <label className="form-label">Maximum Discount</label>
                <div className="input-with-prefix">
                  <span className="input-prefix">PKR</span>
                  <input
                    type="number"
                    name="maxDiscount"
                    value={formData.maxDiscount}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="Optional"
                    min="0"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="form-section">
            <label className="form-label">Minimum Order Amount</label>
            <div className="input-with-prefix">
              <span className="input-prefix">PKR</span>
              <input
                type="number"
                name="minOrderAmount"
                value={formData.minOrderAmount}
                onChange={handleChange}
                className="form-input"
                placeholder="Optional"
                min="0"
              />
            </div>
          </div>

          <div className="form-section">
            <label className="form-label">Validity Period <span className="required">*</span></label>
            <div className="date-inputs">
              <div className="date-input-wrapper">
                <span className="date-icon">📅</span>
                <input
                  type="date"
                  name="validFrom"
                  value={formData.validFrom}
                  onChange={handleChange}
                  className={`form-input ${errors.validFrom ? 'error' : ''}`}
                />
              </div>
              <span className="date-separator">to</span>
              <div className="date-input-wrapper">
                <span className="date-icon">📅</span>
                <input
                  type="date"
                  name="validTill"
                  value={formData.validTill}
                  onChange={handleChange}
                  className={`form-input ${errors.validTill ? 'error' : ''}`}
                />
              </div>
            </div>
            {errors.validFrom && <span className="error-message">{errors.validFrom}</span>}
            {errors.validTill && <span className="error-message">{errors.validTill}</span>}
          </div>

          <div className="form-section">
            <label className="form-label">Usage Limits</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="unlimitedUses"
                  checked={formData.unlimitedUses}
                  onChange={(e) => setFormData(prev => ({ ...prev, unlimitedUses: true, usageLimit: null }))}
                />
                <span>Unlimited uses</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="unlimitedUses"
                  checked={!formData.unlimitedUses}
                  onChange={(e) => setFormData(prev => ({ ...prev, unlimitedUses: false }))}
                />
                <span>Limited to:</span>
                <input
                  type="number"
                  name="usageLimit"
                  value={formData.usageLimit || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, usageLimit: e.target.value, unlimitedUses: false }))}
                  className="form-input inline-input"
                  placeholder="100"
                  min="1"
                  disabled={formData.unlimitedUses}
                />
                <span>uses</span>
              </label>
            </div>
            <label className="checkbox-option">
              <input
                type="checkbox"
                name="perUserLimit"
                checked={formData.perUserLimit}
                onChange={handleChange}
              />
              <span>One time per user (limit each customer to single use)</span>
            </label>
          </div>

          <div className="form-section">
            <label className="form-label">Applicable To</label>
            <div className="radio-group">
              <label className="radio-option">
                <input
                  type="radio"
                  name="applicableTo"
                  value="all"
                  checked={formData.applicableTo === 'all'}
                  onChange={handleChange}
                />
                <span>All items</span>
              </label>
              <label className="radio-option">
                <input
                  type="radio"
                  name="applicableTo"
                  value="categories"
                  checked={formData.applicableTo === 'categories'}
                  onChange={handleChange}
                />
                <span>Specific categories</span>
              </label>
            </div>
            
            {formData.applicableTo === 'categories' && (
              <div className="category-selector">
                <label className="category-selector-label">Select Categories</label>
                <div className="category-checkboxes">
                  {availableCategories.map(category => (
                    <label key={category} className="category-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.applicableCategories.includes(category)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData(prev => ({
                              ...prev,
                              applicableCategories: [...prev.applicableCategories, category]
                            }));
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              applicableCategories: prev.applicableCategories.filter(c => c !== category)
                            }));
                          }
                          if (errors.applicableCategories) {
                            setErrors(prev => ({ ...prev, applicableCategories: '' }));
                          }
                        }}
                      />
                      <span>{category}</span>
                    </label>
                  ))}
                </div>
                {errors.applicableCategories && (
                  <span className="error-message">{errors.applicableCategories}</span>
                )}
                {formData.applicableCategories.length === 0 && !errors.applicableCategories && (
                  <div className="helper-text" style={{ color: '#6C757D', marginTop: 8 }}>
                    Select one or more categories
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-section">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`form-textarea ${errors.description ? 'error' : ''}`}
              placeholder="Describe this offer for customers..."
              rows="4"
              maxLength={200}
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
            <div className="char-counter">{formData.description.length}/200</div>
          </div>

          <div className="form-section">
            <label className="form-label">Terms & Conditions (Optional)</label>
            <textarea
              name="terms"
              value={formData.terms}
              onChange={handleChange}
              className="form-textarea"
              placeholder="Enter terms and conditions..."
              rows="4"
            />
          </div>

          <div className="form-section">
            <label className="toggle-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
                className="large-toggle"
              />
              <div>
                <div className="toggle-label-text">Activate Coupon</div>
                <div className="toggle-helper-text">Coupon will be active immediately</div>
              </div>
            </label>
          </div>

          <div className="modal-footer">
            <button type="button" className="footer-btn cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="footer-btn save-btn">
              Save Coupon
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateEditCouponModal;

